// coaching — the onboarding/coaching model: two orthogonal axes + the pure
// surface derivation. (IDD f95f4542, supersedes the workspaceLifecycle naming.)
//
// Coaching is NOT one state. It is a function of TWO independent axes:
//   · WorkspaceStatus  — what the workspace IS (per-workspace, domain-derived)
//   · AuthorExperience — who the author IS  (per-author, device-local)
// The surface to show is a pure function of the pair, combined HERE in the shell
// view-model (onion: the two axes meet in the shell, never in the core). Pure:
// no I/O, no mutation; the one time-dependency (Stale) takes an injected `now`
// so the clock stays at the boundary, never inside a fold.

// === Perspective — whose picture of the workspace ============================
// A viewer-scoped discriminant so a fold's answer can't be read as another
// viewer's. `ThisAuthor` is what coaching reads; `Organisation` is modelled for
// an admin surface but not yet fed by an org-scoped read (single-tenant MVP).

export type ThisAuthor = { readonly type: "this_author" };
export type Organisation = { readonly type: "organisation" };
export type Perspective = ThisAuthor | Organisation;

export const thisAuthor: ThisAuthor = { type: "this_author" };
export const organisation: Organisation = { type: "organisation" };

// === WorkspaceStatus — per-workspace, DOMAIN-derived =========================
// `Fresh` vs `Active` is a pure perspective-scoped fold over the event log.
// `Stale` (content but dormant) is TIME-dependent, so it is decided with an
// injected `now`, NOT from events alone. Each case carries its perspective so
// "whose picture" travels with the value.

export type FreshWorkspace = {
    readonly type: "fresh";
    readonly perspective: Perspective;
};
export type ActiveWorkspace = {
    readonly type: "active";
    readonly perspective: Perspective;
};
export type StaleWorkspace = {
    readonly type: "stale";
    readonly perspective: Perspective;
};
export type WorkspaceStatus = FreshWorkspace | ActiveWorkspace | StaleWorkspace;

// The projected facts the status fold reads, already perspective-scoped by the
// read that produced them (never a raw tuple). `lastActivityAt` (ms) feeds the
// Active-vs-Stale split; `null` when the workspace is empty.
export type WorkspaceFootprint = {
    readonly resourceCount: number;
    readonly conversationCount: number;
    readonly lastActivityAt: number | null;
};

// The injected clock for the Active-vs-Stale split — time stays at the shell,
// never in a fold. Omitted in MVP callers, so the result is only ever
// `Fresh | Active` until a real clock + threshold are wired.
export type StaleClock = {
    readonly now: number;
    readonly staleAfterMs: number;
};

// Empty → Fresh; content dormant past the threshold → Stale (only when a clock
// is supplied); otherwise Active.
export const workspaceStatus = (
    perspective: Perspective,
    footprint: WorkspaceFootprint,
    clock?: StaleClock,
): WorkspaceStatus => {
    const empty =
        footprint.resourceCount === 0 && footprint.conversationCount === 0;
    if (empty) return { type: "fresh", perspective };
    if (
        clock !== undefined &&
        footprint.lastActivityAt !== null &&
        clock.now - footprint.lastActivityAt > clock.staleAfterMs
    )
        return { type: "stale", perspective };
    return { type: "active", perspective };
};

export const isFresh = (status: WorkspaceStatus): boolean =>
    status.type === "fresh";

// === AuthorExperience — per-author, DEVICE-LOCAL =============================
// NOT a domain fact: it spans workspaces and never enters the event log (like
// `tourSeen` / the theme), until a User-profile aggregate exists. Derived from
// a real dismissal affordance, never a fabricated usage heuristic:
//   default          → Beginner
//   tips dismissed   → Advanced      ("stäng av tips", the existing opt-out)
// (D28: the former Intermediate rung keyed on "visa inte igen" on the resting
// coachmark. That coachmark is gone, the guided tour now runs only when the
// author asks for it, so nothing could reach the rung any more.)

export type Beginner = { readonly type: "beginner" };
export type Advanced = { readonly type: "advanced" };
export type AuthorExperience = Beginner | Advanced;

// The device-local flags that drive the ladder. Each distinct intent gets its
// own flag, never one boolean whose value swaps meaning.
export type CoachingDismissals = {
    // "stäng av tips" (the existing tips opt-out) → the author is comfortable.
    readonly tipsDismissed: boolean;
};

export const authorExperience = (
    dismissals: CoachingDismissals,
): AuthorExperience =>
    dismissals.tipsDismissed ? { type: "advanced" } : { type: "beginner" };

// === Arc prominence — how loudly the outcome arc presents itself ============
// A pure derivation from `AuthorExperience`, NOT a raw settings toggle
// (settings = an unmade decision; the ladder already IS the decision). A
// Beginner is shown the arc prominently — it is new, and the spine
// (source → material → package) is the thing they most need oriented. Once the
// author has turned off tips, they have shown they don't need the spotlight,
// so the arc turns quiet (present, not shouting).

export type ProminentArc = { readonly type: "prominent" };
export type QuietArc = { readonly type: "quiet" };
export type ArcProminence = ProminentArc | QuietArc;

export const arcProminence = (experience: AuthorExperience): ArcProminence =>
    experience.type === "beginner" ? { type: "prominent" } : { type: "quiet" };

// The guided tour is not derived here. Owner call Dm9 ruled out a first-run
// auto-scrim (pushy), and since D28 the tour runs only on the landing's explicit
// "Take a guided tour" click, for any author on any workspace. There is no
// (experience × status) decision left to make, so CoachHost starts it directly.

// === Activation progress — the newcomer's position on the authoring spine =====
// A pure 3-bit snapshot of how far THIS author has walked the source → material
// → package spine. Domain-derived (the shell projects it from the Library),
// never a stored onboarding flag — deleting the last source flips `hasSource`
// back to false, so a surface reading it can never lie about progress. The
// onboarding view-models (app/onboarding.ts) fold this into the arc + checklist.

export type ActivationProgress = {
    readonly hasSource: boolean;
    readonly hasMaterial: boolean;
    readonly hasPackage: boolean;
};

// === Onboarding surface — WHICH centre surface a coached newcomer sees ========
// Orthogonal to the guided tour (the on-request spotlight walk-through in
// CoachHost): this picks the CENTRE content, a pure
// function of the two coaching axes plus activation progress. A DISTINCT case
// per intent, never a flag: the first-run welcome, the empty-project nudge, the
// post-first-generation "aha", or nothing. `empty_project` is modelled for the
// multi-project future (a named project that holds no work) but is NOT yet
// produced by the MVP decider below — single-project MVP has no per-project
// footprint, so a fresh workspace already reads as `first_run_welcome`. Stated
// shortcut (lifecycle-design): the case exists in the type so the surface can be
// wired later without reshaping this union.

export type FirstRunWelcomeSurface = { readonly type: "first_run_welcome" };
export type EmptyProjectSurface = { readonly type: "empty_project" };
export type PostFirstGenerationSurface = {
    readonly type: "post_first_generation";
};
export type NoOnboardingSurface = { readonly type: "none" };
export type OnboardingSurface =
    | FirstRunWelcomeSurface
    | EmptyProjectSurface
    | PostFirstGenerationSurface
    | NoOnboardingSurface;

// Only a beginner is guided (advanced has opted down). A fresh
// workspace → the first-run welcome, which retires the moment real work exists
// (status leaves `fresh`). An active beginner whose first material has taken
// shape but nothing is packaged yet → the post-first-generation aha (review/edit
// what appeared; export is named, never pushed on an unreviewed result).
export const onboardingSurface = (
    experience: AuthorExperience,
    status: WorkspaceStatus,
    progress: ActivationProgress,
): OnboardingSurface => {
    if (experience.type !== "beginner") return { type: "none" };
    if (status.type === "fresh") return { type: "first_run_welcome" };
    if (progress.hasMaterial && !progress.hasPackage)
        return { type: "post_first_generation" };
    return { type: "none" };
};
